import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Shield,
  Clock,
  FileText,
  HeartPulse,
  Wallet,
  UserCheck,
  MessageCircle,
  CheckCircle2,
  AlertTriangle,
  ChevronDown,
  HelpCircle,
} from "lucide-react";

const LOGO_ICON = "https://customer-assets.emergentagent.com/job_direito-trabalho-sp/artifacts/8b5v8061_IconOnly_Transparent_NoBuffer.png";
const WHATSAPP_LINK = "https://wa.me/5511917611575?text=Olá! Gostaria de uma consulta sobre Direito Previdenciário.";

const PrevidenciarioPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      pergunta: "Tive meu benefício negado pelo INSS. O que fazer?",
      resposta: "Você pode entrar com recurso administrativo junto ao INSS (no prazo de 30 dias) ou ingressar diretamente com ação judicial. Muitas vezes, a Justiça Federal concede benefícios que foram indevidamente negados na via administrativa."
    },
    {
      pergunta: "Qual a diferença entre aposentadoria por idade e por tempo de contribuição?",
      resposta: "A aposentadoria por idade exige idade mínima (65 anos para homens e 62 para mulheres) + 15 anos de contribuição. Já a aposentadoria por tempo de contribuição (regras de transição após a Reforma) considera o tempo trabalhado, com diferentes regras como pedágio e pontos."
    },
    {
      pergunta: "O que é aposentadoria especial?",
      resposta: "É a aposentadoria concedida a trabalhadores expostos a agentes nocivos (físicos, químicos ou biológicos) que prejudicam a saúde. Permite aposentadoria com 15, 20 ou 25 anos de contribuição, conforme o grau de nocividade da atividade."
    },
    {
      pergunta: "Posso pedir revisão da minha aposentadoria?",
      resposta: "Sim. Se houve erro no cálculo do benefício, tempo de contribuição não computado ou aplicação incorreta das regras, você pode pedir revisão. O prazo decadencial é de 10 anos a partir do primeiro pagamento do benefício."
    },
    {
      pergunta: "O que é BPC/LOAS?",
      resposta: "O Benefício de Prestação Continuada (BPC) é um salário mínimo mensal pago a idosos (65+) ou pessoas com deficiência que comprovem não possuir meios de prover a própria subsistência. A renda familiar por pessoa deve ser inferior a 1/4 do salário mínimo."
    },
    {
      pergunta: "Recebi alta do INSS mas ainda não consigo trabalhar. O que fazer?",
      resposta: "Você pode pedir a prorrogação do benefício dentro de 30 dias ou, se a alta já foi confirmada, ingressar com novo pedido de auxílio-doença. Em casos urgentes, cabe ação judicial com pedido de tutela de urgência para restabelecimento do benefício."
    },
  ];

  const servicos = [
    { icon: <Clock className="w-5 h-5" />, title: "Aposentadorias", desc: "Por idade, tempo de contribuição, especial e do professor" },
    { icon: <HeartPulse className="w-5 h-5" />, title: "Auxílio-Doença", desc: "Requerimento e recursos em caso de indeferimento" },
    { icon: <Shield className="w-5 h-5" />, title: "Aposentadoria por Invalidez", desc: "Para incapacidade total e permanente" },
    { icon: <Wallet className="w-5 h-5" />, title: "BPC/LOAS", desc: "Benefício para idosos e pessoas com deficiência" },
    { icon: <UserCheck className="w-5 h-5" />, title: "Pensão por Morte", desc: "Benefício para dependentes" },
    { icon: <FileText className="w-5 h-5" />, title: "Revisão de Benefícios", desc: "Correção de valores e cálculos" },
  ];

  return (
    <>
      <Helmet>
        <title>Advogado Previdenciário | Aposentadoria INSS</title>
        <meta name="description" content="Advogado previdenciário especializado. Concessão e revisão de benefícios, aposentadorias, auxílios e defesa junto ao INSS." />
        <meta name="keywords" content="advogado previdenciário, aposentadoria INSS, benefício negado, direito previdenciário" />
      </Helmet>

      <div>
        {/* Hero */}
        <section className="pt-32 pb-20 bg-gradient-to-br from-purple-50 to-white relative overflow-hidden">
          <div className="absolute right-0 top-1/2 -translate-y-1/2 opacity-[0.03] pointer-events-none">
            <img src={LOGO_ICON} alt="" className="w-[500px] h-auto" style={{ filter: "brightness(0)" }} />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <p className="text-purple-700 font-medium tracking-wider text-sm mb-4">DIREITO PREVIDENCIÁRIO</p>
            <h1
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 max-w-3xl"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Advogado <span className="text-purple-700">Previdenciário</span>
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mb-8">
              Atuação na concessão e revisão de benefícios previdenciários, aposentadorias, 
              auxílios e defesa administrativa e judicial perante o INSS.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-purple-700 hover:bg-purple-800 text-white px-8 py-4 rounded font-medium transition-all">
                <MessageCircle className="w-5 h-5" />
                Fale com Especialista
              </a>
              <Link to="/contato"
                className="inline-flex items-center justify-center border-2 border-gray-300 hover:border-purple-700 text-gray-700 hover:text-purple-700 px-8 py-4 rounded font-medium transition-all">
                Enviar Mensagem
              </Link>
            </div>
          </div>
        </section>

        {/* Quando procurar */}
        <section className="py-16 bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Quando Procurar um Advogado Previdenciário?
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Se você teve um benefício negado pelo INSS, deseja se aposentar e não sabe como, 
                precisa revisar o valor de um benefício já concedido ou está enfrentando 
                dificuldades com a perícia médica.
              </p>
            </div>
          </div>
        </section>

        {/* Serviços */}
        <section className="py-20 bg-gray-50 relative overflow-hidden">
          <div className="absolute left-0 bottom-0 opacity-[0.02] pointer-events-none">
            <img src={LOGO_ICON} alt="" className="w-[400px] h-auto" style={{ filter: "brightness(0)" }} />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-10" style={{ fontFamily: "'Playfair Display', serif" }}>
              Nossos Serviços Previdenciários
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {servicos.map((servico, index) => (
                <div key={index} className="bg-white p-6 rounded-lg border border-gray-100 hover:shadow-md transition-all">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center text-purple-700 mb-4">
                    {servico.icon}
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">{servico.title}</h4>
                  <p className="text-gray-600 text-sm">{servico.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Diferencial */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Teve seu Benefício Negado pelo INSS?
                </h2>
                <p className="text-gray-600 mb-6">
                  Muitos benefícios são negados indevidamente pelo INSS. Se você teve seu pedido 
                  indeferido, não desista! É possível recorrer administrativamente ou judicialmente.
                </p>
                <div className="space-y-4">
                  {[
                    "Análise gratuita do seu caso",
                    "Recursos administrativos junto ao INSS",
                    "Ações judiciais para concessão",
                    "Revisão de benefícios incorretos",
                    "Acompanhamento de perícias médicas",
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-purple-50 p-8 rounded-xl border border-purple-100">
                <AlertTriangle className="w-10 h-10 text-amber-500 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Atenção aos Prazos</h3>
                <p className="text-gray-600 mb-6">
                  Existem prazos para recorrer de decisões do INSS. Não deixe seu direito prescrever!
                </p>
                <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded font-medium transition-all w-full">
                  <MessageCircle className="w-5 h-5" />
                  Analisar Meu Caso
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <HelpCircle className="w-10 h-10 text-purple-700 mx-auto mb-4" />
              <h2 
                className="text-2xl md:text-3xl font-bold text-gray-900 mb-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Perguntas Frequentes
              </h2>
              <p className="text-gray-600">
                Tire suas principais dúvidas sobre Direito Previdenciário
              </p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div 
                  key={index}
                  className="border border-gray-200 rounded-lg overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-5 text-left bg-white hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-medium text-gray-900 pr-4">{faq.pergunta}</span>
                    <ChevronDown 
                      className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform ${
                        openFaq === index ? "rotate-180" : ""
                      }`} 
                    />
                  </button>
                  {openFaq === index && (
                    <div className="px-5 pb-5 pt-0">
                      <p className="text-gray-600 leading-relaxed">{faq.resposta}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-purple-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Precisa de Ajuda com o INSS?
            </h2>
            <p className="text-purple-100 mb-8 max-w-xl mx-auto">
              Entre em contato e garanta seus direitos previdenciários.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded font-medium transition-all">
                <MessageCircle className="w-5 h-5" />
                Falar pelo WhatsApp
              </a>
              <Link to="/contato"
                className="inline-flex items-center justify-center border-2 border-white/30 hover:border-white text-white px-8 py-4 rounded font-medium transition-all">
                Enviar Mensagem
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default PrevidenciarioPage;
